import { memo, useMemo } from 'react';

import { type CustomFieldTypeName } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { NullState } from '~/components/NullState';
import { RecentlyDeletedRecoverAssetModal } from '~/components/RecentlyDeleted/components/modals/RecentlyDeletedRecoverAssetModal';
import { RecentlyDeletedRecoverBoardModal } from '~/components/RecentlyDeleted/components/modals/RecentlyDeletedRecoverBoardModal';
import { RecentlyDeletedRecoverCustomFieldModal } from '~/components/RecentlyDeleted/components/modals/RecentlyDeletedRecoverCustomFieldModal';
import { RecentlyDeletedRecoverLibraryModal } from '~/components/RecentlyDeleted/components/modals/RecentlyDeletedRecoverLibraryModal';
import { RecentlyDeletedAssetTableRow } from '~/components/RecentlyDeleted/components/RecentlyDeletedAssetTableRow';
import { RecentlyDeletedBoardTableRow } from '~/components/RecentlyDeleted/components/RecentlyDeletedBoardTableRow';
import { RecentlyDeletedCustomFieldTableRow } from '~/components/RecentlyDeleted/components/RecentlyDeletedCustomFieldTableRow';
import { RecentlyDeletedLibraryTableRow } from '~/components/RecentlyDeleted/components/RecentlyDeletedLibraryTableRow';
import { RecentlyDeletedTableHeader } from '~/components/RecentlyDeleted/components/RecentlyDeletedTableHeader';
import { MIN_TABLE_WIDTH } from '~/components/RecentlyDeleted/constants/tableWidth';
import { useRecentlyDeleted } from '~/components/RecentlyDeleted/hooks/queries/useRecentlyDeleted';
import { useDeletionRecoveryWindow } from '~/components/RecentlyDeleted/hooks/useDeletionRecoveryWindow';
import { useRecentlyDeletedQueryParam } from '~/components/RecentlyDeleted/hooks/useRecentlyDeletedQueryParam';
import { useRecentlyDeletedTableRows } from '~/components/RecentlyDeleted/hooks/useRecentlyDeletedTableRows';
import { TableView, type TableViewProps } from '~/components/TableView';
import { TablePageContainer } from '~/components/TableView/TablePageContainer';
import {
  assetIdsBeingRestoredSelector,
  boardIdsBeingRestoredSelector,
  libraryIdsBeingRestoredSelector,
} from '~/store/tasks/selectors';
import { useAirSelector } from '~/utils/ReduxUtils';

export type RecentlyDeletedTableViewProps = Pick<TableViewProps, 'scrollElement' | 'scrollElementRef'>;

export const RecentlyDeletedTableView = memo(({ scrollElement, scrollElementRef }: RecentlyDeletedTableViewProps) => {
  const [showRecentlyDeletedRecoverAssetModal] = useAirModal(RecentlyDeletedRecoverAssetModal);
  const [showRecentlyDeletedRecoverBoardModal] = useAirModal(RecentlyDeletedRecoverBoardModal);
  const [showRecentlyDeletedRecoverLibraryModal] = useAirModal(RecentlyDeletedRecoverLibraryModal);
  const [showRecentlyDeletedRecoverCustomFieldModal] = useAirModal(RecentlyDeletedRecoverCustomFieldModal);
  const { recoveryWindow } = useDeletionRecoveryWindow();
  const { filteredTypes, sortField, sortDirection } = useRecentlyDeletedQueryParam();
  const { data, fetchNextPage, hasNextPage, isEmpty, isFetchingNextPage, isInitialLoading } = useRecentlyDeleted({
    itemTypes: filteredTypes,
    sortField,
    sortDirection,
    enabled: filteredTypes.length > 0,
  });
  const boardsIdsBeingRestored = useAirSelector(boardIdsBeingRestoredSelector);
  const libraryIdsBeingRestored = useAirSelector(libraryIdsBeingRestoredSelector);
  const assetIdsBeingRestored = useAirSelector(assetIdsBeingRestoredSelector);

  const filteredData = useMemo(
    () =>
      data?.filter(({ item }) => {
        if (item.itemType === 'board' && item.board.id) {
          return !boardsIdsBeingRestored.includes(item.board.id);
        }

        if (item.itemType === 'library' && item.library.id) {
          return !libraryIdsBeingRestored.includes(item.library.id);
        }

        if (item.itemType === 'asset' && item.asset.id) {
          return !assetIdsBeingRestored.includes(item.asset.id);
        }

        return true;
      }),
    [data, assetIdsBeingRestored, boardsIdsBeingRestored, libraryIdsBeingRestored],
  );

  const rows = useRecentlyDeletedTableRows({
    data: filteredData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading,
    renderAssetRow: ({ data, rowStyle }) => {
      return (
        <RecentlyDeletedAssetTableRow
          dateDeleted={data.createdAt}
          deletedFrom={data.asset.parentBoards}
          recoveryWindow={recoveryWindow}
          deletedBy={data.deletedBy}
          key={data.id}
          onRecoverAsset={() =>
            showRecentlyDeletedRecoverAssetModal({
              id: data.asset.id,
              title: data.asset.title,
              parentBoards: data.asset.parentBoards,
            })
          }
          style={rowStyle}
          thumbnail={data.asset.thumbnail ?? undefined}
          ext={data.asset.ext}
          status={data.asset.status}
          title={data.asset.title}
          versionCount={data.asset.versionCount}
        />
      );
    },
    renderBoardRow: ({ data, rowStyle }) => {
      return (
        <RecentlyDeletedBoardTableRow
          dateDeleted={data.createdAt}
          deletedFrom={data.board.parentBoard}
          recoveryWindow={recoveryWindow}
          deletedBy={data.deletedBy}
          key={data.id}
          onRecoverBoard={() =>
            showRecentlyDeletedRecoverBoardModal({
              id: data.board.id,
              title: data.board.title,
              hasDeletedParent: data.board.parentBoard?.inactive || data.board.library?.inactive || false,
            })
          }
          style={rowStyle}
          thumbnail={data.board.thumbnail ?? undefined}
          title={data.board.title}
        />
      );
    },
    renderLibraryRow: ({ data, rowStyle }) => {
      return (
        <RecentlyDeletedLibraryTableRow
          dateDeleted={data.createdAt}
          recoveryWindow={recoveryWindow}
          deletedBy={data.deletedBy}
          key={data.id}
          onRecoverLibrary={() =>
            showRecentlyDeletedRecoverLibraryModal({
              id: data.library.id,
              title: data.library.title,
              hasDuplicateTitle: data.library.hasDuplicateLibraryTitle,
            })
          }
          style={rowStyle}
          title={data.library.title}
          icon={data.library.icon}
        />
      );
    },
    renderCustomFieldRow: ({ data, rowStyle }) => {
      return (
        <RecentlyDeletedCustomFieldTableRow
          dateDeleted={data.createdAt}
          recoveryWindow={recoveryWindow}
          deletedBy={data.deletedBy}
          key={data.id}
          onRecoverCustomField={() =>
            showRecentlyDeletedRecoverCustomFieldModal({
              id: data.customField.id,
              name: data.customField.name,
              fieldType: data.customField.type as CustomFieldTypeName,
            })
          }
          style={rowStyle}
          title={data.customField.name}
          fieldType={data.customField.type as CustomFieldTypeName}
        />
      );
    },
  });

  if (isEmpty) {
    return <NullState description="No deleted items found." data-testid="RECENTLY_DELETED_NULL_STATE" />;
  }

  return (
    <TablePageContainer>
      <TableView
        canScrollHorizontally
        minTableWidth={MIN_TABLE_WIDTH}
        renderHeaders={(ref) => <RecentlyDeletedTableHeader ref={ref} />}
        rows={rows}
        scrollElement={scrollElement}
        scrollElementRef={scrollElementRef}
      />
    </TablePageContainer>
  );
});

RecentlyDeletedTableView.displayName = 'RecentlyDeletedTableView';
